/**
 * <ElementBadge element="Fire" />
 *
 * Small, reusable chip that shows an element with its palette. Used by the
 * Combat Log, Codex, Forging preview, Enchanter preview, and Inventory
 * elemental affix rows. Do not build one-off element chips elsewhere — extend
 * this component instead.
 */
import { ELEMENT_META, normalizeElement } from "../constants/elements";
import {
  Flame, Snowflake, Zap, Skull, Moon, Sun,
  Sparkles, Mountain, Droplet, Wind,
} from "lucide-react";

const ICONS = {
  flame: Flame, snowflake: Snowflake, zap: Zap, skull: Skull,
  moon: Moon, sun: Sun, sparkles: Sparkles, mountain: Mountain,
  droplet: Droplet, wind: Wind,
};

export function ElementBadge({ element, value, showLabel = true, size = "sm" }) {
  const el = normalizeElement(element);
  if (!el) return null;
  const meta = ELEMENT_META[el];
  const Icon = ICONS[meta.icon] || Sparkles;
  const dims = size === "lg"
    ? { icon: 16, pad: "px-2.5 py-1 text-[11px]" }
    : { icon: 12, pad: "px-1.5 py-0.5 text-[9px]" };
  return (
    <span
      className={`inline-flex items-center gap-1 border font-mono tracking-widest uppercase ${dims.pad}`}
      style={{
        borderColor: meta.color,
        color: meta.color,
        textShadow: `0 0 6px ${meta.glow}`,
        backgroundColor: "rgba(0,0,0,0.35)",
      }}
      data-testid={`element-badge-${el.toLowerCase()}`}
      title={meta.label}
    >
      <Icon size={dims.icon} strokeWidth={1.6} />
      {showLabel && <span>{meta.label}</span>}
      {value !== undefined && <span className="ml-0.5">{value}</span>}
    </span>
  );
}

export function ElementList({ elements, showLabel = false, size = "sm" }) {
  const items = (elements || []).map(normalizeElement).filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1" data-testid="element-list">
      {items.map((el) => (
        <ElementBadge key={el} element={el} showLabel={showLabel} size={size} />
      ))}
    </div>
  );
}
